import { uniq } from 'lodash-es';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Checkbox, Drawer, message, Modal, Radio } from 'antd';
import {
  CloseOutlined,
  ExclamationCircleFilled,
  LeftOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { ProductImages } from '../ProductImages';
import { SimpleInputNumber } from '../ToDoBundle/FormItem';
import { formatNumber } from 'utils/numberFormat';
import {
  calculateCurrency,
  CalculateRes,
  createCancel,
  getTradeEligibilityList,
  logisticsList,
  tradeList,
} from 'services/api/conversation';
import { useDebounceEffect } from 'ahooks';
import { useTranslate } from '@tolgee/react';
import useModal from 'hooks/useModal';
import styled from '@emotion/styled';
import { DefaultButton } from 'components/LightConversation/components/DefaultButton';
import isEqual from 'react-fast-compare';

const selectEnable = false;

type CancelData = Record<
  string,
  Record<
    string,
    {
      orderLineItemIds: string[];
      orderLineList: Conversation.Trade.ReverseEligibilityOrderLine[];
    }
  >
>;

function CancelOrderContent({
  cancelData,
  order,
  storeId,
  onUpdate,
  onClose,
}: {
  cancelData: CancelData;
  order: Conversation.Trade.TradeWithLogistics;
  storeId: string | number;
  onUpdate: (trade: Conversation.Trade.TradeWithLogistics) => void;
  onClose: () => void;
}) {
  const { internalTradeId, externalTradeId, items, unit, platform, internalBuyerId } = order;

  const { t } = useTranslate();

  const [step, setStep] = useState<1 | 2>(1);

  const [selectedReason, setSelectedReason] = useState<string>();

  const [selectedIds, setSelectedIds] = useState<Conversation.Trade.ReverseEligibilityOrderLine[]>(
    [],
  );

  const reasons = Object.keys(cancelData);

  const { reasonData, allChecked, indeterminate, ids, skusData } = useMemo(() => {
    if (!selectedReason) {
      return {
        reasonData: undefined,
        allChecked: false,
        indeterminate: false,
        ids: [],
        skusData: [],
      };
    }

    const reasonData = cancelData[selectedReason];

    const ids = Object.values(reasonData).reduce<Conversation.Trade.ReverseEligibilityOrderLine[]>(
      (res, { orderLineItemIds, orderLineList }) => {
        return [
          ...res,
          ...orderLineList,
          ...orderLineItemIds.map((orderLineItemId) => ({
            orderLineItemId,
            subOrderLineItemId: '',
          })),
        ];
      },
      [],
    );

    const allChecked = ids.every((id) => selectedIds.some((_) => isEqual(_, id)));
    const indeterminate = !allChecked && ids.some((id) => selectedIds.some((_) => isEqual(_, id)));

    const skusData = Object.entries(reasonData).reduce<
      (
        | {
            type: 'main';
            item: Conversation.Trade.Product;
            orderLineItemIds: string[];
            selected: string[];
          }
        | {
            type: 'sub';
            item: Conversation.Trade.CombinedListingSku;
            orderLineList: Conversation.Trade.ReverseEligibilityOrderLine[];
            selected: Conversation.Trade.ReverseEligibilityOrderLine[];
          }
      )[]
    >((res, [skuId, { orderLineItemIds, orderLineList }]) => {
      const item = items.find(({ externalSkuId }) => externalSkuId === skuId);
      if (!item) {
        return res;
      }
      if (!orderLineList.length) {
        res.push({
          type: 'main',
          item,
          orderLineItemIds,
          selected: orderLineItemIds.filter((id) =>
            selectedIds.some((_) => _.orderLineItemId === id),
          ),
        });
        return res;
      }
      return orderLineList.reduce((res, cur) => {
        const sub = item?.combinedListingSkus?.find(({ subOrderLineItemId }) =>
          subOrderLineItemId.includes(cur.subOrderLineItemId),
        );
        if (!sub) {
          return res;
        }
        const target = res.find((_) => _.type === 'sub' && _.item.skuId === sub.skuId);
        if (target && target.type === 'sub') {
          target.orderLineList = [...target.orderLineList, cur];
          target.selected = target.orderLineList.filter((id) =>
            selectedIds.some((_) => isEqual(_, id)),
          );
        } else {
          res.push({
            type: 'sub',
            item: sub,
            orderLineList: [cur],
            selected: [cur].filter((id) => selectedIds.some((_) => isEqual(_, id))),
          });
        }
        return res;
      }, res);
    }, []);

    return {
      reasonData,
      allChecked,
      indeterminate,
      ids,
      skusData,
    };
  }, [cancelData, selectedReason, items, selectedIds]);

  const [loading, setLoading] = useState(false);
  const [calculateRes, setCalculateRes] = useState<CalculateRes>();

  const calculateRef = useRef(0);

  useDebounceEffect(() => {
    if (selectedReason && selectedIds.length) {
      setLoading(true);
      calculateRef.current += 1;
      const currentCalculate = calculateRef.current;
      calculateCurrency({
        internalTradeId,
        requestType: 'CANCEL',
        reasonName: selectedReason,
        orderLineList: selectedIds,
      })
        .then((res) => {
          if (calculateRef.current === currentCalculate) {
            setLoading(false);
            setCalculateRes(res);
          }
        })
        .catch(() => {
          if (calculateRef.current === currentCalculate) {
            setLoading(false);
            setCalculateRes(undefined);
          }
        });
    } else {
      setCalculateRes(undefined);
    }
  }, [selectedReason, selectedIds]);

  useEffect(() => {
    return () => {
      setStep(1);
      setSelectedReason(undefined);
      setSelectedIds([]);
      setCalculateRes(undefined);
      calculateRef.current = 0;
    };
  }, [cancelData]);

  if (!reasons.length) {
    return <></>;
  }

  const step1 = (
    <Step1>
      <header>
        <Button type="text" size="small" icon={<CloseOutlined />} onClick={onClose} />
        <h2>{t('scene.chatWorkspace.returnRefundFlow.cancelOrder.placeholder.selectReason')}</h2>
        <div>{`${t('domain.order.field.orderId')}: ${externalTradeId}`}</div>
      </header>
      <div>
        <CancellationWarning role="alert">
          <div className="cancellation-warning-title">
            <ExclamationCircleFilled />
            <span>
              {t(
                'scene.chatWorkspace.returnRefundFlow.cancelOrder.warning.sellerResponsibilityTitle',
              )}
            </span>
          </div>
          <ul>
            <li>
              {t('scene.chatWorkspace.returnRefundFlow.cancelOrder.warning.sellerResponsibility')}
            </li>
            <li>
              {t(
                'scene.chatWorkspace.returnRefundFlow.cancelOrder.warning.buyerRequestedCancellation',
              )}
            </li>
          </ul>
        </CancellationWarning>
        <ul>
          {reasons.map((reason) => {
            const onChecked = () => {
              setSelectedReason(reason);
              setSelectedIds(
                !selectEnable
                  ? Object.values(cancelData[reason]).reduce<
                      Conversation.Trade.ReverseEligibilityOrderLine[]
                    >((res, { orderLineItemIds, orderLineList }) => {
                      return [
                        ...res,
                        ...orderLineList,
                        ...orderLineItemIds.map((orderLineItemId) => ({
                          orderLineItemId,
                          subOrderLineItemId: '',
                        })),
                      ];
                    }, [])
                  : Object.values(cancelData[reason]).reduce(
                      (res, ids) => res + ids.orderLineItemIds.length + ids.orderLineList.length,
                      0,
                    ) === 1
                  ? [
                      ...Object.values(cancelData[reason])[0].orderLineList,
                      ...Object.values(cancelData[reason])[0].orderLineItemIds.map(
                        (orderLineItemId) => ({
                          orderLineItemId,
                          subOrderLineItemId: '',
                        }),
                      ),
                    ]
                  : [],
              );
            };
            return (
              <li key={reason} onClick={onChecked}>
                <Radio checked={selectedReason === reason}>
                  <div>{t(`tiktok.returnReason.${reason}`)}</div>
                  {reason.includes('out_of_stock') && (
                    <span>{t('tiktok.returnReasonTips.out_of_stock')}</span>
                  )}
                  {reason.includes('reason_wrong_price') && (
                    <span>{t('tiktok.returnReasonTips.reason_wrong_price')}</span>
                  )}
                  {reason.includes('address') && (
                    <span>{t('tiktok.returnReasonTips.address')}</span>
                  )}
                  {reason.includes('reason_buyer_requested_cancellation') && (
                    <span>{t('tiktok.returnReasonTips.reason_buyer_requested_cancellation')}</span>
                  )}
                </Radio>
              </li>
            );
          })}
        </ul>
        <footer>
          <DefaultButton onClick={onClose}>{t('common.action.cancel')}</DefaultButton>
          <Button type="primary" disabled={!selectedReason} onClick={() => setStep(2)}>
            {t('common.action.next')}
            <RightOutlined />
          </Button>
        </footer>
      </div>
    </Step1>
  );

  if (!selectedReason || !reasonData) {
    return <Content step={1}>{step1}</Content>;
  }

  const formatter = (num: number | string) => `${unit} ${formatNumber(num, '0,0.00')}`;

  const step2 = (
    <Step2>
      <header>
        <Button type="text" size="small" icon={<LeftOutlined />} onClick={() => setStep(1)} />
        <div>
          <span>
            {t('scene.chatWorkspace.returnRefundFlow.cancelOrder.placeholder.selectReason')}
          </span>
          <h2>{t('scene.chatWorkspace.returnRefundFlow.cancelOrder.action.editDetails')}</h2>
        </div>
      </header>
      {selectedReason.includes('reason_buyer_requested_cancellation') && (
        <Alert
          closable
          banner
          showIcon
          type="info"
          message={
            <div>
              <div>
                {t('tiktok.returnReason.seller_cancel_paid_reason_buyer_requested_cancellation')}
              </div>
              <span>
                {t(
                  'scene.chatWorkspace.returnRefundFlow.cancelOrder.confirmation.approvesCancellation',
                )}
              </span>
            </div>
          }
        />
      )}
      {selectedReason.includes('out_of_stock') && (
        <Alert
          closable
          banner
          showIcon
          type="info"
          message={
            <div>
              <div>
                {t(
                  'scene.chatWorkspace.returnRefundFlow.cancelOrder.validation.awaitingShipmentOnly',
                )}
              </div>
            </div>
          }
        />
      )}
      <section>
        <div>
          <h3>
            {t('scene.chatWorkspace.returnRefundFlow.cancelOrder.label.productCount', {
              count: ids.length,
            })}
          </h3>
          {ids.length > 1 && selectEnable && (
            <Checkbox
              checked={allChecked}
              indeterminate={indeterminate}
              onChange={({ target: { checked } }) => setSelectedIds(checked ? ids : [])}
            >
              {t('common.action.selectAll')}
            </Checkbox>
          )}
          <ul>
            {skusData.map((data) => {
              if (data.type === 'main') {
                const { item, orderLineItemIds, selected } = data;
                const { itemPic, itemName, sellerSku, unitPrice } = item;
                return (
                  <li>
                    <ProductImages images={[itemPic]} platform={platform} />
                    <section>
                      <h4>{itemName}</h4>
                      <p>{`${t('SKU货号')}: ${sellerSku || '-'}`}</p>
                    </section>
                    <span>
                      <section>
                        {formatter(unitPrice)}
                        <span>x{orderLineItemIds.length}</span>
                      </section>
                      {ids.length > 1 && selectEnable && (
                        <SimpleInputNumber
                          min={0}
                          max={orderLineItemIds.length}
                          value={selected.length}
                          onChange={(value) =>
                            setSelectedIds(
                              uniq([
                                ...selectedIds.filter(
                                  (id) => !orderLineItemIds.includes(id.orderLineItemId),
                                ),
                                ...orderLineItemIds.slice(0, value).map((orderLineItemId) => ({
                                  orderLineItemId,
                                  subOrderLineItemId: '',
                                })),
                              ]),
                            )
                          }
                        />
                      )}
                    </span>
                  </li>
                );
              }
              if (data.type === 'sub') {
                const { item, orderLineList, selected } = data;
                const { image, title, sellerSku } = item;
                return (
                  <li>
                    <ProductImages images={[image]} platform={platform} />
                    <section>
                      <h4>{title}</h4>
                      <p>{`${t('SKU货号')}: ${sellerSku || '-'}`}</p>
                    </section>
                    <span>
                      <section>
                        <span>x{orderLineList.length}</span>
                      </section>
                      {ids.length > 1 && selectEnable && (
                        <SimpleInputNumber
                          min={0}
                          max={orderLineList.length}
                          value={selected.length}
                          onChange={(value) =>
                            setSelectedIds(
                              uniq([
                                ...selectedIds.filter((_) =>
                                  orderLineList.every((id) => !isEqual(id, _)),
                                ),
                                ...orderLineList.slice(0, value),
                              ]),
                            )
                          }
                        />
                      )}
                    </span>
                  </li>
                );
              }
              return null;
            })}
          </ul>
        </div>
        <span>
          <h3>{t('scene.chatWorkspace.returnRefundFlow.cancelOrder.section.summary')}</h3>
          <ul>
            <li>
              <div>{`${t(
                'scene.chatWorkspace.returnRefundFlow.cancelOrder.section.cancelingProducts',
              )}: ${selectedIds.length}`}</div>
              <span>{formatter(calculateRes?.refundTotal || 0)}</span>
            </li>
            {calculateRes && [
              <li>
                <div>
                  {t('scene.chatWorkspace.returnRefundFlow.cancelOrder.label.productRefund')}
                </div>
                <span>{formatter(calculateRes.refundSubtotal)}</span>
              </li>,
              <li>
                <div>
                  {t('scene.chatWorkspace.returnRefundFlow.cancelOrder.label.shippingFeeRefund')}
                </div>
                <span>{formatter(calculateRes.refundShippingFee)}</span>
              </li>,
              <li>
                <div>{t('scene.chatWorkspace.returnRefundFlow.cancelOrder.label.taxRefund')}</div>
                <span>{formatter(calculateRes.refundTax)}</span>
              </li>,
            ]}
          </ul>
          <div>{`${t(
            'scene.chatWorkspace.returnRefundFlow.cancelOrder.label.cancellationReason',
          )}: ${t(`tiktok.returnReason.${selectedReason}`)}`}</div>
          <Button
            block
            type="primary"
            disabled={!calculateRes || !selectedIds.length}
            loading={loading}
            onClick={() => {
              Modal.confirm({
                title: t(
                  'scene.chatWorkspace.returnRefundFlow.cancelOrder.confirm.cancelItemCount',
                  { count: selectedIds.length },
                ),
                content: t(
                  'scene.chatWorkspace.returnRefundFlow.cancelOrder.warning.doNotShipAfterCancel',
                ),
                onOk: () => {
                  setLoading(true);
                  createCancel({
                    internalTradeId,
                    orderLineList: selectedIds,
                    cancelReason: selectedReason,
                  })
                    .then(() => {
                      message.success(
                        t('scene.chatWorkspace.orderTab.cancelOrder.feedback.operationSuccess'),
                      );
                      onClose();

                      setTimeout(() => {
                        tradeList({
                          page_no: 1,
                          page_size: 1,
                          internal_buyer_id: internalBuyerId,
                          internal_store_id: storeId,
                          external_trade_ids: externalTradeId,
                        })
                          .then((res) => {
                            const { tradeList: [trade] = [] } = res;

                            if (!trade) {
                              return;
                            }

                            return logisticsList({
                              internalStoreId: storeId,
                              internal_trade_ids: internalTradeId,
                              needPackageData: true,
                            }).then((res) => {
                              return getTradeEligibilityList({
                                internalTradeIds: [internalTradeId],
                              }).then(({ orderEligibilityList }) => {
                                return onUpdate({
                                  ...trade,
                                  logisticsList: res.data.data,
                                  eligibility: orderEligibilityList[0],
                                  ['_queryParams' as any]: (order as any)._queryParams,
                                });
                              });
                            });
                          })
                          .catch(() => {});
                      }, 2000);
                    })
                    .catch(() => {
                      message.error(t('common.feedback.operationFailed'));
                    })
                    .finally(() => {
                      setLoading(false);
                    });
                },
              });
            }}
          >
            {t('common.action.confirm')}
          </Button>
        </span>
      </section>
    </Step2>
  );

  return (
    <Content step={step}>
      {step1}
      {step2}
    </Content>
  );
}

function CancelOrder({
  order,
  storeId,
  onUpdate,
}: {
  order: Conversation.Trade.TradeWithLogistics;
  storeId: string | number;
  onUpdate: (trade: Conversation.Trade.TradeWithLogistics) => void;
}) {
  const { t } = useTranslate();

  const { visible, show, hide } = useModal();

  const { eligibility: { skuEligibility } = {}, items } = order;

  const cancelData = useMemo(() => {
    return (skuEligibility || []).reduce<CancelData>((res, { skuId, lineItemEligibility }) => {
      const sku =
        items.find(({ externalSkuId }) => externalSkuId === skuId) ||
        items.find(({ combinedListingSkus }) =>
          combinedListingSkus?.some((_) => _.skuId === skuId),
        );
      if (!sku) {
        return res;
      }
      const _skuId = sku.externalSkuId;
      return lineItemEligibility.reduce(
        (res, { requestType, orderLineItemIds, orderLineList, availableReasonNames }) => {
          if (requestType !== 'CANCEL') {
            return res;
          }
          return availableReasonNames.reduce((res, reason) => {
            if (!res[reason]) {
              res[reason] = {
                [_skuId]: {
                  orderLineItemIds,
                  orderLineList,
                },
              };
            } else if (!res[reason][_skuId]) {
              res[reason][_skuId] = {
                orderLineItemIds,
                orderLineList,
              };
            } else {
              res[reason][_skuId] = {
                orderLineItemIds: uniq([
                  ...res[reason][_skuId].orderLineItemIds,
                  ...orderLineItemIds,
                ]),
                orderLineList: [...res[reason][_skuId].orderLineList, ...orderLineList],
              };
            }
            return res;
          }, res);
        },
        res,
      );
    }, {});
  }, [skuEligibility, items]);

  if (!Object.keys(cancelData).length) {
    return <></>;
  }

  const drawer = (
    <Drawer visible={visible} width={888} title={false} closable={false} onClose={hide}>
      <Container>
        <CancelOrderContent
          cancelData={cancelData}
          order={order}
          storeId={storeId}
          onUpdate={onUpdate}
          onClose={hide}
        />
      </Container>
    </Drawer>
  );

  const button = (
    <Button type="primary" size="small" onClick={show}>
      {t('scene.chatWorkspace.returnRefundFlow.cancelOrder.action.cancelOrder')}
    </Button>
  );

  return (
    <>
      {button}
      {drawer}
    </>
  );
}

const Container = styled.div`
  margin: -24px;
  height: calc(100% + 48px);
  overflow: hidden;
`;

const Content = styled.div<{ step: 1 | 2 }>`
  display: flex;
  width: 200%;
  height: 100%;
  margin-left: ${({ step }) => (step === 2 ? '-100%' : '0')};
  overflow: hidden;
  transition: all 0.3s ease-out;
  > div {
    width: 50%;
    height: 100%;
  }
`;

const CancellationWarning = styled.div`
  margin-bottom: 16px;
  padding: 16px;
  border-radius: 8px;
  background: #ffeeee;
  color: #47494f;
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;

  .cancellation-warning-title {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 16px;
    font-weight: 500;
    line-height: 24px;

    > .anticon {
      flex-shrink: 0;
      color: #ee5151;
      font-size: 14px;
    }
  }

  > ul {
    margin: 12px 0 0;
    padding: 0;
    list-style: none;

    > li::before {
      content: '· ';
    }

    > li + li {
      margin-top: 6px;
    }
  }

  [data-color-mode='dark'] & {
    background: var(--fett-surface-error-bg);
    color: var(--fett-text-secondary);

    .anticon {
      color: var(--fett-surface-error-text);
    }
  }
`;

const Step1 = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 16px;
  background: var(--fett-bg-subtle);
  > header {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    > * {
      margin-right: 8px;
    }
    > button {
      background: var(--fett-border-default);
    }
    > h2 {
      margin-bottom: 0;
    }
    > div {
      margin-top: 6px;
      font-size: 12px;
      color: var(--fett-text-tertiary);
    }
  }
  > div {
    flex: 0 1 auto;
    margin-top: 16px;
    padding: 16px;
    background: var(--fett-bg-elevated);
    border-radius: 4px;
    overflow: auto;
    > ul {
      margin: 0;
      padding: 0;
      list-style: none;
      > li {
        margin-bottom: 16px;
        padding: 12px;
        border: solid 1px var(--fett-border-default);
        border-radius: 4px;
        cursor: pointer;
        > label > span > div {
          font-weight: 500;
          color: var(--fett-text-primary);
        }
        > label > span > span {
          display: block;
          margin-top: 6px;
          font-weight: 400;
          color: var(--fett-text-tertiary);
        }
        .ant-radio-inner {
          margin-top: 0;
        }
      }
    }
    > footer {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      > button {
        margin-left: 16px;
      }
    }
  }
`;

const Step2 = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 16px;
  background: var(--fett-bg-subtle);
  > header {
    flex: 0 0 auto;
    display: flex;
    margin-bottom: 16px;
    > * {
      margin-right: 8px;
    }
    > button {
      background: var(--fett-border-default);
    }
    > div {
      > span {
        font-size: 12px;
        color: var(--fett-text-tertiary);
      }
      > h2 {
        margin-bottom: 0;
      }
    }
  }
  > .ant-alert {
    flex: 0 0 auto;
    align-items: flex-start;
    margin-bottom: 16px;
    padding: 16px;
    background: var(--fett-border-default);
    border-radius: 4px;
    > span {
      margin-top: 4px;
      color: var(--fett-text-tertiary);
    }
    .ant-alert-message > div {
      > div {
        font-weight: 500;
        color: var(--fett-text-primary);
      }
      > span {
        display: block;
        margin-top: 6px;
        color: var(--fett-text-tertiary);
      }
    }
  }
  > section {
    flex: 1 0 0;
    display: flex;
    align-items: flex-start;
    overflow: hidden;
    > div {
      flex: 1 0 0;
      max-height: 100%;
      padding: 16px;
      background: var(--fett-bg-elevated);
      border-radius: 4px;
      overflow: auto;
      overflow: auto;
      > label {
        margin-bottom: 12px;
      }
      > ul {
        margin: 0;
        padding: 0;
        list-style: none;
        > li {
          display: flex;
          padding: 12px;
          background: var(--fett-bg-subtle);
          border-radius: 4px;
          &:not(:last-of-type) {
            margin-bottom: 16px;
          }
          > div {
            margin-right: 8px;
          }
          > section {
            flex: 1 0 0;
            overflow: hidden;
            > h4 {
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
              margin-bottom: 4px;
            }
            > p {
              margin-bottom: 0;
              font-size: 12px;
              color: var(--fett-text-tertiary);
            }
          }
          > span {
            flex: 0 0 auto;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            align-items: flex-end;
            margin-left: 6px;
            > section {
              display: flex;
              flex-direction: column;
              align-items: flex-end;
            }
          }
        }
      }
    }
    > span {
      flex: 0 0 auto;
      width: 252px;
      max-height: 100%;
      margin-left: 16px;
      padding: 14px 16px;
      border-radius: 4px;
      background: var(--fett-bg-elevated);
      overflow: auto;
      > ul {
        margin: 0;
        padding: 0;
        list-style: none;
        > li {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
          &:first-of-type {
            font-weight: 500;
          }
          > div {
            flex: 1 0 0;
          }
          > span {
            flex: 0 0 auto;
          }
        }
      }
      > div {
        margin-bottom: 12px;
        color: var(--fett-text-tertiary);
      }
    }
  }
`;

export default CancelOrder;
